import { BookingState, VoucherBookingCountdown } from '@products/models/booking.model';
import { getCountdownTimestamp } from '@shared/app-utils';
import { cloneDeep } from 'lodash';

/**
 * Checks if state voucher is expired
 * @param voucherBookingCountdown Current state voucher countdown
 * @returns True if current state voucher expired
 */
const isVoucherExpired = (voucherBookingCountdown: VoucherBookingCountdown): boolean => {
  const currentTimestamp: number = Date.now();
  return voucherBookingCountdown && currentTimestamp > voucherBookingCountdown.expiryTimestamp;
};

/**
 * Set product booking countdown
 * @description Set product booking countdown
 * @param state
 * @param props
 * @returns State
 */
export const setProductBookingCountdown = (state: BookingState, props: { releaseInMinutes: number }) => {
  const expiryTimestamp: number = props.releaseInMinutes && getCountdownTimestamp(props.releaseInMinutes);

  return {
    ...state,
    countdown: {
      ...state.countdown,
      product: expiryTimestamp
    }
  };
};

/**
 * Set voucher product booking countdown
 * @description Set voucher product booking countdown
 * @param state
 * @param props
 * @returns State
 */
export const setVoucherProductBookingCountdown = (
  state: BookingState,
  props: { voucherBookingCountdowns: VoucherBookingCountdown[] }
) => {
  const voucherBookingCountdowns: VoucherBookingCountdown[] = cloneDeep(props.voucherBookingCountdowns);
  const currentAddedVoucherIsAnonymous: boolean = voucherBookingCountdowns.some(
    (voucherCountdown) => voucherCountdown.isAnonymous
  );
  const stateHasAnonymousVouchers: boolean = state.countdown.voucher.some(
    (voucherCountdown) => voucherCountdown.isAnonymous
  );

  const vouchersCountdown: VoucherBookingCountdown[] =
    !stateHasAnonymousVouchers && currentAddedVoucherIsAnonymous
      ? voucherBookingCountdowns
      : [
          ...state.countdown.voucher,
          ...voucherBookingCountdowns.filter(
            (voucherCountdown) =>
              !state.countdown.voucher.some((stateVoucher) => stateVoucher.voucherCode === voucherCountdown.voucherCode)
          )
        ];

  const productCountdown: number = voucherBookingCountdowns[0].expiryTimestamp;

  return {
    ...state,
    countdown: {
      ...state.countdown,
      voucher: vouchersCountdown,
      product: productCountdown
    }
  };
};

/**
 * Remove voucher product booking countdown
 * @description Remove voucher product booking countdown
 * @param state
 * @param props
 * @returns State
 */
export const removeVoucherProductBookingCountdown = (
  state: BookingState,
  props: { voucherBookingCountdowns: VoucherBookingCountdown[]; releaseInMinutes: number }
) => {
  const { voucherBookingCountdowns, releaseInMinutes } = props;

  let voucherExpired = false;
  let newProductCountdownState: number = null;

  const newVoucherBookingCountdownsState: VoucherBookingCountdown[] = state.countdown.voucher.filter(
    (stateVoucherCountdown) =>
      !voucherBookingCountdowns.some((voucherCountdown) => {
        const isSameVoucherCode: boolean = voucherCountdown.voucherCode === stateVoucherCountdown.voucherCode;

        if (!voucherExpired && isSameVoucherCode) {
          voucherExpired = isVoucherExpired(stateVoucherCountdown);
        }

        return isSameVoucherCode;
      })
  );

  if (state.list.length) {
    if (voucherExpired) {
      newProductCountdownState = state.countdown.product;
    } else {
      newProductCountdownState = getCountdownTimestamp(releaseInMinutes);
    }
  }

  return {
    ...state,
    countdown: {
      ...state.countdown,
      voucher: cloneDeep(newVoucherBookingCountdownsState),
      product: newProductCountdownState
    }
  };
};

/**
 * Remove product booking countdowns
 * @description Remove product booking countdowns
 * @param state
 * @returns State
 */
export const removeProductBookingCountdowns = (state: BookingState) => ({
  ...state,
  countdown: {
    product: null,
    voucher: []
  }
});
